import { lazy } from 'react';
import { Navigate, RouteObject, createBrowserRouter } from 'react-router-dom';
import WrapperRouteComponent from './config';
import Middleware from '@/middleware';
import { PAGE_TITLES } from '@/constants/titles';
import AuthLayout from '@/layouts/auth.layout';
import MainLayout from '@/layouts/main.layout';
import { ProtectedRoute } from '@/components/HOC/ProtectedRoute';
import { AuthWrapper } from '@/components/HOC/AuthWrapper';
import { SmartRedirect } from '@/components/HOC/SmartRedirect';

// Lazy load các components
//module
const DashboardPage = lazy(() => import('@/modules/dashboard'));
const BillingPage = lazy(() => import('@/modules/billing'));
const CouponPage = lazy(() => import('@/modules/coupon'));
const OrdersPage = lazy(() => import('@/modules/orders'));
const OrderDetailPage = lazy(() => import('@/modules/orders/detail'));
const OrderCategoryPage = lazy(() => import('@/modules/orders/detail/order-category'));
const CustomerPage = lazy(() => import('@/modules/customer'));

const CustomerDetailPage = lazy(() => import('@/modules/customer/detail'));
const CompanyPage = lazy(() => import('@/modules/company'));
const CatalogPage = lazy(() => import('@/modules/catalog'));
const FormFieldsPage = lazy(() => import('@/modules/form-fields'));
const AuditLogPage = lazy(() => import('@/modules/audit-log'));
const NotificationsPage = lazy(() => import('@/modules/notifications'));
const PaymentSettingsPage = lazy(() => import('@/modules/payments-settings'));

const ComingSoonPage = lazy(
  () => import(/* webpackChunkName: "coming-soon"*/ '@/pages/ComingSoon'),
);

//Auth
const LoginPage = lazy(() => import(/* webpackChunkName: "login"*/ '@/pages/Auth/Login'));
const ProfilePage = lazy(() => import(/* webpackChunkName: "profile"*/ '@/pages/Profile'));
const SettingPage = lazy(() => import(/* webpackChunkName: "settings"*/ '@/pages/Settings'));
const ForgotPasswordPage = lazy(
  () => import(/* webpackChunkName: "forgot-password"*/ '@/pages/Auth/ForgotPassword'),
);

const VerifyCode = lazy(
  () => import(/* webpackChunkName: "verify-code"*/ '@/pages/Auth/VerifyCode'),
);
const ResetPasswordPage = lazy(
  () => import(/* webpackChunkName: "reset-password"*/ '@/pages/Auth/ResetPassword'),
);

//not-found
const NotFoundPage = lazy(() => import('@/pages/NotFound'));

// Định nghĩa các routes
const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <AuthWrapper>
        <WrapperRouteComponent title="">
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        </WrapperRouteComponent>
      </AuthWrapper>
    ),
    children: [
      {
        index: true,
        element: <SmartRedirect />,
      },
      {
        path: 'admin/dashboard',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.DASHBOARD}>
            <DashboardPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/invoices',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.BILLING}>
            <BillingPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/coupons',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.COUPON}>
            <CouponPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/orders',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.ORDERS}>
            <OrdersPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/orders/:ordersId',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.ORDERS}>
            <OrderDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/orders/:ordersId/:ordersCategoryId',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.ORDERS}>
            <OrderCategoryPage />
          </WrapperRouteComponent>
        ),
      },

      {
        path: '/admin/profile',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.PROFILE}>
            <ProfilePage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/settings',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.SETTINGS}>
            <SettingPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/customers',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.CUSTOMER}>
            <CustomerPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/catalog',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.CATALOG}>
            <CatalogPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/form-fields',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.FORM_FIELDS}>
            <FormFieldsPage />
          </WrapperRouteComponent>
        ),
      },

      {
        // Catch-all route cho các dynamic sidebar menus chưa có page riêng
        path: '*',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.COMING_SOON}>
            <ComingSoonPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/customers/:customerId',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.CUSTOMER}>
            <CustomerDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/companies',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.COMPANY}>
            <CompanyPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/audit',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.AUDIT}>
            <AuditLogPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/notifications',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.NOTIFICATIONS}>
            <NotificationsPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/admin/payment-settings',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.PAYMENTSETTINGS}>
            <PaymentSettingsPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <WrapperRouteComponent title="">
        <Middleware mode="non-login">
          <AuthLayout />
        </Middleware>
      </WrapperRouteComponent>
    ),
    children: [
      {
        path: 'login',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <LoginPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.FORGOT_PASSWORD}>
            <ForgotPasswordPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'verify-code',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.VERIFY_CODE}>
            <VerifyCode />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.RESET_PASSWORD}>
            <ResetPasswordPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },

  {
    path: '/not-found',
    element: (
      <WrapperRouteComponent title={PAGE_TITLES.NOT_FOUND}>
        <NotFoundPage />
      </WrapperRouteComponent>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/not-found" replace />,
  },
];

// Export router
export const router = createBrowserRouter(routes);
